import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useOrdersByDepartureStatus } from 'hooks/useOrdersByDepartureStatus'
import LesaPurchaseSummary from '../../SelectPurchase/LesaPurchaseSummary/LesaPurchaseSummary'
import Action from './Action'
import { isChip, isComponent } from '../../../utils/actionsUtils'
import { arrayToMap } from '../../../../client/lib/array/arrayUtils'
import LesaPurchaseSummarySkeleton from '../../SelectPurchase/LesaPurchaseSummary/Common/LesaPurchaseSummarySkeleton'
import ModalContext from 'contexts/ModalContext'
import LesaOTPModal from 'supportAssistant/components/Chat/MessageMeta/LesaOTPModal'
import { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getFirstName } from 'selectors/accountSelectors'
import Group from 'components/utils/Group'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { getOrderBookingNumbers, getOrdersWithAirportTransfers } from 'selectors/orderSelectors'

const Container = styled(Group)`
  padding-left: ${rem(32)};
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(4)};
  height: 0;
`

interface Props {
  metaContent: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload;
}

function MetaComponents(props: Props) {
  const { metaContent, payload } = props

  const dispatch = useAppDispatch()

  const customerOrdersData = useOrdersByDepartureStatus()
  const { loading, upcoming, previous } = customerOrdersData

  const showModal = useContext(ModalContext)

  const customerId = useAppSelector((state: App.State) => state.auth.account.memberId)
  const firstName = useAppSelector(getFirstName)
  const ordersWithAirportTransfers = useAppSelector(getOrdersWithAirportTransfers)
  const orderBookingNumbers = useAppSelector(getOrderBookingNumbers)

  const [loginSuccessful, setLoginSuccessful] = useState<boolean>(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentOrder = useMemo(() => [...upcoming, ...previous].find(order => order.id === payload?.id_orders), [payload?.id_orders])

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [loading, currentOrder, metaContent, upcoming, previous])

  useEffect(() => {
    if (loginSuccessful && customerId && firstName) {
      const updatedPayload = {
        ...payload,
        customerId,
        firstName,
      }
      dispatch(addChatMessage('Login successful', { action: 'listDefaultActions', payload: updatedPayload }))
    }
  }, [customerId, dispatch, firstName, loginSuccessful, payload])

  const componentsMap = useMemo(() => arrayToMap(metaContent.filter(action => isComponent(action)), a => a.type), [metaContent])
  const actionsChips = useMemo(() => metaContent.filter(isChip), [metaContent])

  const isListOrdersAction = !!componentsMap.get('list-orders')
  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')
  const isListOrdersWithAirportTransfersAction = !!componentsMap.get('list-orders-with-airport-transfers')
  const isListOrdersWithExtraGuestsAction = !!componentsMap.get('list-orders-extra-guests')
  const isOTPModalAction = !!componentsMap.get('otp-login')

  const ordersWithAirportTransfersIds = ordersWithAirportTransfers.map(order => order.id)

  const upcomingOrdersWithAirportTransfers = upcoming.filter(order => ordersWithAirportTransfersIds.includes(order.id))
  const previousOrdersWithAirportTransfers = previous.filter(order => ordersWithAirportTransfersIds.includes(order.id))

  const ordersWithExtraGuestsIds = orderBookingNumbers
    .filter(order => 'canAddExtraGuests' in order && order.canAddExtraGuests)
    .map(order => order.id)

  if ((isListOrdersAction || isListFlightsOrdersAction || isListOrdersWithAirportTransfersAction || isListOrdersWithExtraGuestsAction) && loading) {
    return <>
      <LesaPurchaseSummarySkeleton />
      <ScrollTo ref={scrollRef} />
    </>
  }

  if (isOTPModalAction) {
    showModal<AccountAccessModalResult>(<LesaOTPModal email={payload?.email as string} maskedPhone={payload?.maskedPhone as string} />).then(async(result) => {
      if (!result?.loggedIn) {
        dispatch(addChatMessage('Login failed', { action: 'listDefaultActions', payload }))
      } else {
        setLoginSuccessful(true)
      }
    })
  }

  return (
    <Container direction="vertical" gap={16}>
      {currentOrder && (!isListOrdersAction && !isListFlightsOrdersAction) && <LesaPurchaseSummary actions={actionsChips} componentsMap={componentsMap} payload={payload} order={currentOrder} />}
      {isListOrdersAction && (
        <Group direction="vertical" gap={8}>
          {upcoming.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcoming.length === 0 && previous.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListFlightsOrdersAction && (
        <Group direction="vertical" gap={8}>
          {upcoming.filter(order => order.hasFlight).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcoming.length === 0 && previous.filter(order => order.hasFlight).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListOrdersWithAirportTransfersAction && (
        <Group direction="vertical" gap={8}>
          {upcomingOrdersWithAirportTransfers?.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcomingOrdersWithAirportTransfers?.length === 0 && previousOrdersWithAirportTransfers?.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListOrdersWithExtraGuestsAction && (
        <Group direction="vertical" gap={8}>
          {upcoming.filter(order => ordersWithExtraGuestsIds.includes(order.id)).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcoming.length === 0 && previous.filter(order => ordersWithExtraGuestsIds.includes(order.id)).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {!currentOrder &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {actionsChips.map((action, index) => (
            <Action action={action} key={`${action.text}-${index}`} payload={payload} />
          ))}
        </Group>}
      <ScrollTo ref={scrollRef} />
    </Container>
  )
}

export default MetaComponents
